import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Actions, Bubble, Sender } from '@ant-design/x';
import { Result } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import { PageMeta } from '@app/components/meta/Meta';
import useSendChatMessage from '@app/hooks/webchat/useSendChatMessage';
import useStartChatSession from '@app/hooks/webchat/useStartChatSession';
import { getApiErrors } from '@app/services/api';
import type { BubbleItemType } from '@ant-design/x';
import type { ComponentRef } from 'react';
import { StyledButton } from '@app/components/button/Button';
import Logo from '@images/logo.png';
import { WebchatSkeleton } from './components/WebchatSkeleton';
import type { BubbleListRef } from '@ant-design/x/es/bubble';
import { IoAdd } from 'react-icons/io5';
import { StyledTooltip } from '@app/components/tooltip/Tooltip';
import { BsStars } from 'react-icons/bs';

type WebchatMessage = {
    id: string;
    role: 'user' | 'ai';
    content: string;
    failed?: boolean;
    errorMessage?: string;
};

const TYPEWRITER_STEP = 3;
const TYPEWRITER_INTERVAL_MS = 20;

// react-markdown's contentRender must own its own reveal state — Bubble's built-in
// `typing` animation only engages when contentRender returns a plain string, and
// markdown rendering always returns an element instead.
const TypewriterMarkdown = ({ content }: { content: string }) => {
    const [revealedLength, setRevealedLength] = useState(() =>
        Math.min(TYPEWRITER_STEP, content.length)
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setRevealedLength((prev) => {
                const next = prev + TYPEWRITER_STEP;
                if (next >= content.length) {
                    clearInterval(interval);
                    return content.length;
                }
                return next;
            });
        }, TYPEWRITER_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [content]);

    return <ReactMarkdown>{content.slice(0, revealedLength)}</ReactMarkdown>;
};

const renderMarkdown = (content: string) => <TypewriterMarkdown content={content} />;

const aiAvatar = (
    <div className="webchat-avatar">
        <BsStars size={30} />
    </div>
);

export const Webchat = () => {
    const { agentId } = useParams<{ agentId: string }>();
    const session = useStartChatSession(agentId ?? '');
    const sendMessage = useSendChatMessage();
    const [draft, setDraft] = useState('');
    const [messages, setMessages] = useState<WebchatMessage[]>([]);
    const senderRef = useRef<ComponentRef<typeof Sender>>(null);
    const wasPendingRef = useRef(false);
    const listRef = useRef<BubbleListRef>(null);

    const sessionId = session.data?.session_id;

    // Sender disables its input while a reply is pending, which blurs it — restore
    // focus once the reply settles (success or failure) so the visitor can keep typing.
    useEffect(() => {
        if (wasPendingRef.current && !sendMessage.isPending) {
            senderRef.current?.focus();
        }
        wasPendingRef.current = sendMessage.isPending;
    }, [sendMessage.isPending]);

    const appendAgentReply = (content: string) => {
        setMessages((prev) => [...prev, { id: uuidv4(), role: 'ai', content }]);
    };

    const markMessageFailed = (id: string, error: unknown) => {
        const response = (error as { response?: { data?: { error?: string | string[] } } })
            ?.response;
        // getApiErrors(..., true) always resolves to a single string (or false) at runtime;
        // its return type isn't narrowed by the `single` argument.
        const errorMessage =
            (getApiErrors(response?.data?.error, true) as string) || 'Failed to send';
        setMessages((prev) =>
            prev.map((message) =>
                message.id === id ? { ...message, failed: true, errorMessage } : message
            )
        );
    };

    const sendChatMessage = (id: string, content: string) => {
        if (!agentId || !sessionId) return;
        sendMessage.mutate(
            { agentId, sessionId, message: content },
            {
                onSuccess: (data) => appendAgentReply(data.message),
                onError: (error) => markMessageFailed(id, error),
            }
        );
    };

    const handleSubmit = (content: string) => {
        const trimmed = content.trim();
        if (!trimmed || sendMessage.isPending) return;

        const id = uuidv4();
        setMessages((prev) => [...prev, { id, role: 'user', content: trimmed }]);
        setDraft('');
        sendChatMessage(id, trimmed);
        listRef.current?.scrollTo({ top: 'bottom' });
    };

    const handleNewChat = () => {
        if (sendMessage.isPending) return;
        setMessages([]);
        setDraft('');
        session.refetch();
    };

    const handleRetry = (message: WebchatMessage) => {
        if (sendMessage.isPending) return;
        setMessages((prev) =>
            prev.map((item) =>
                item.id === message.id ? { ...item, failed: false, errorMessage: undefined } : item
            )
        );
        sendChatMessage(message.id, message.content);
    };

    if (session.isError) {
        return (
            <div className="webchat-error">
                <PageMeta title="Webchat" />
                <Result
                    status="error"
                    title="We couldn't start this chat"
                    subTitle="Something went wrong connecting you to this agent."
                    extra={
                        <StyledButton
                            type="primary"
                            onClick={() => session.refetch()}
                            loading={session.isFetching}>
                            Try again
                        </StyledButton>
                    }
                />
            </div>
        );
    }

    if (!session.data) {
        return (
            <div className="webchat-container">
                <PageMeta title="Webchat" />
                <WebchatSkeleton />
            </div>
        );
    }

    const bubbleItems: BubbleItemType[] = [
        {
            key: 'greeting',
            role: 'ai',
            content: session.data.message,
            contentRender: renderMarkdown,
        },
        ...messages.map((message) => ({
            key: message.id,
            role: message.role,
            content: message.content,
            ...(message.role === 'ai' ? { contentRender: renderMarkdown } : {}),
            ...(message.failed
                ? {
                      footer: (
                          <div className="webchat-message-failed">
                              <span>{message.errorMessage || 'Failed to send'}</span>
                              <Actions
                                  items={[
                                      {
                                          key: 'retry',
                                          icon: <RedoOutlined />,
                                          label: 'Retry',
                                          onItemClick: () => handleRetry(message),
                                      },
                                  ]}
                              />
                          </div>
                      ),
                  }
                : {}),
        })),
        ...(sendMessage.isPending
            ? [
                  {
                      key: 'pending-reply',
                      role: 'ai',
                      content: '',
                      loading: true,
                  },
              ]
            : []),
    ];

    return (
        <div className="webchat-container">
            <PageMeta title="Webchat" />
            <div className="webchat-header">
                <div className="brand-lockup">
                    <img src={session?.data?.logo || Logo} className="logo" />
                    {/* <span className="brand-divider"></span>
                    <span className="agent-status">
                        <i></i>Online
                    </span> */}
                </div>
                {sessionId ? (
                    <nav className="top-actions">
                        <StyledTooltip title="Start New Chat">
                            <StyledButton
                                type="default"
                                color="default"
                                shape="circle"
                                size="large"
                                icon={<IoAdd />}
                                onClick={handleNewChat}
                                disabled={sendMessage.isPending}
                                loading={session.isFetching}
                            />
                        </StyledTooltip>
                    </nav>
                ) : null}
            </div>
            <Bubble.List
                ref={listRef}
                className="webchat-message-list"
                items={bubbleItems}
                role={{
                    user: { placement: 'end', variant: 'filled', shape: 'round' },
                    ai: { placement: 'start', variant: 'filled', shape: 'round', avatar: aiAvatar },
                }}
            />
            <div className="webchat-message-sender">
                <Sender
                    ref={senderRef}
                    value={draft}
                    onChange={setDraft}
                    onSubmit={handleSubmit}
                    disabled={sendMessage.isPending}
                    loading={sendMessage.isPending}
                    placeholder="Type a message..."
                    allowSpeech
                />
            </div>
        </div>
    );
};

export default Webchat;
